import { useState, useEffect } from 'react';
import type { JourneyItemTemplateChatFirestore, JourneyItemFieldToCollect } from '../../services/journeyTypes';
import { JourneyItemFormFields } from './JourneyItemFormFields';
import { useJourneyItemForm } from './useJourneyItemForm';
import { JourneyItemFormActions } from './JourneyItemFormActions';
import QuestFieldsToCollectManager from '../../../Quests/components/fields-to-collect/QuestFieldsToCollectManager';
import ErrorDisplay from '../../../../components/ErrorDisplay';

interface ChatSpecificFormData {
    aiPromptGuidance: string;
    aiOpeningStatementGuidance: string;
    aiClosingStatementGuidance: string;
    objective: string;
    fieldsToCollect: JourneyItemFieldToCollect[];
}

interface ChatJourneyItemFormProps {
    itemTemplate?: JourneyItemTemplateChatFirestore;
    initialOrder: number;
    onSave: (item: JourneyItemTemplateChatFirestore) => void;
    onSaveAndContinue?: (item: JourneyItemTemplateChatFirestore) => void;
    onCancel: () => void;
}

export const ChatJourneyItemForm = ({ itemTemplate, initialOrder, onSave, onSaveAndContinue, onCancel }: ChatJourneyItemFormProps) => {
    const {
        commonFormData,
        loading,
        error,
        handleCommonChange,
        handleSubmit,
        handleSaveAndContinue,
    } = useJourneyItemForm({
        itemTemplate,
        initialOrder,
        defaultProgressType: 'fixed',
    });

    const [specificFormData, setSpecificFormData] = useState<ChatSpecificFormData>({
        aiPromptGuidance: '',
        aiOpeningStatementGuidance: '',
        aiClosingStatementGuidance: '',
        objective: '',
        fieldsToCollect: [],
    });

    const [hasUnsavedFieldChanges, setHasUnsavedFieldChanges] = useState(false);

    useEffect(() => {
        if (itemTemplate && itemTemplate.id) {
            setSpecificFormData({
                aiPromptGuidance: itemTemplate.aiPromptGuidance,
                aiOpeningStatementGuidance: itemTemplate.aiOpeningStatementGuidance,
                aiClosingStatementGuidance: itemTemplate.aiClosingStatementGuidance,
                objective: itemTemplate.objective,
                fieldsToCollect: itemTemplate.fieldsToCollect,
            });
        }
    }, [itemTemplate]);

    const handleSpecificChange = (field: string, value: string | boolean | JourneyItemFieldToCollect[]) => {
        setSpecificFormData((prev) => ({ ...prev, [field]: value }));
    };

    const handleChange = (field: string, value: string | number | boolean | JourneyItemFieldToCollect[]) => {
        if (field in commonFormData) {
            handleCommonChange(field as keyof typeof commonFormData, value as string | number | boolean);
        } else {
            handleSpecificChange(field, value as string | boolean | JourneyItemFieldToCollect[]);
        }
    };

    const validateSpecificFields = (): string | null => {
        if (!specificFormData.objective.trim()) {
            return 'Objective is required';
        }
        if (hasUnsavedFieldChanges) {
            return 'Please save or cancel the field you are currently editing before submitting the form';
        }
        return null;
    };

    const createItem = (baseData: Partial<JourneyItemTemplateChatFirestore>): JourneyItemTemplateChatFirestore => {
        const maxProgress = specificFormData.fieldsToCollect.length > 0 ? specificFormData.fieldsToCollect.length : 1;

        const item: JourneyItemTemplateChatFirestore = {
            ...baseData,
            type: 'chat',
            maxProgress,
            aiPromptGuidance: specificFormData.aiPromptGuidance,
            aiOpeningStatementGuidance: specificFormData.aiOpeningStatementGuidance,
            aiClosingStatementGuidance: specificFormData.aiClosingStatementGuidance,
            objective: specificFormData.objective,
            fieldsToCollect: specificFormData.fieldsToCollect,
        } as JourneyItemTemplateChatFirestore;

        return item;
    };

    const onSubmit = handleSubmit(validateSpecificFields, createItem, onSave);
    const onSaveAndContinueClick = onSaveAndContinue ? handleSaveAndContinue(validateSpecificFields, createItem, onSaveAndContinue) : undefined;

    return (
        <form onSubmit={onSubmit} className="space-y-4">
            {error && <ErrorDisplay message={error} />}

            <JourneyItemFormFields
                formData={commonFormData}
                itemType="chat"
                disableProgressType={false}
                onChange={handleChange}
            />

            <h2 className="text-lg font-bold mt-2">Chat-specific fields</h2>
            <hr className="-mt-4" />

            <div>
                <label htmlFor="objective" className="block text-sm font-medium text-gray-700 mb-1">
                    Objective *
                </label>
                <textarea
                    id="objective"
                    value={specificFormData.objective}
                    onChange={(e) => handleChange('objective', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter objective"
                    rows={3}
                    required
                />
            </div>

            <div>
                <label htmlFor="aiPromptGuidance" className="block text-sm font-medium text-gray-700 mb-1">
                    AI Prompt Guidance
                </label>
                <textarea
                    id="aiPromptGuidance"
                    value={specificFormData.aiPromptGuidance}
                    onChange={(e) => handleChange('aiPromptGuidance', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter AI prompt guidance"
                    rows={3}
                />
            </div>

            <div>
                <label htmlFor="aiOpeningStatementGuidance" className="block text-sm font-medium text-gray-700 mb-1">
                    AI Opening Statement Guidance
                </label>
                <textarea
                    id="aiOpeningStatementGuidance"
                    value={specificFormData.aiOpeningStatementGuidance}
                    onChange={(e) => handleChange('aiOpeningStatementGuidance', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter AI opening statement guidance"
                    rows={3}
                />
            </div>

            <div>
                <label htmlFor="aiClosingStatementGuidance" className="block text-sm font-medium text-gray-700 mb-1">
                    AI Closing Statement Guidance
                </label>
                <textarea
                    id="aiClosingStatementGuidance"
                    value={specificFormData.aiClosingStatementGuidance}
                    onChange={(e) => handleChange('aiClosingStatementGuidance', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter AI closing statement guidance"
                    rows={3}
                />
            </div>

            <QuestFieldsToCollectManager
                fields={specificFormData.fieldsToCollect}
                onChange={(fields: JourneyItemFieldToCollect[]) => handleChange('fieldsToCollect', fields)}
                onUnsavedChangesChange={setHasUnsavedFieldChanges}
            />

            {error && <ErrorDisplay message={error} />}

            <JourneyItemFormActions
                onCancel={onCancel}
                onSaveAndContinue={onSaveAndContinueClick}
                loading={loading}
                isEditMode={!!itemTemplate?.id}
            />
        </form>
    );
};
